import React, { useState, useEffect, useContext } from 'react';
import AuthContext from './context/AuthContext';
import { AppModeContext } from './context/AppModeContext';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useModalFix } from './hooks/useModalFix';

// Components
import Navbar from './components/layout/Navbar';
import Sidebar from './components/layout/Sidebar';
import Dashboard from './components/pages/Dashboard';
import Patients from './components/pages/Patients';
import PatientDetail from './components/pages/PatientDetail';
import AddPatient from './components/pages/AddPatient';
import EditPatient from './components/pages/EditPatient';
import PatientArchive from './components/pages/PatientArchive';
import Doctors from './components/pages/Doctors';
import AddDoctor from './components/pages/AddDoctor';

import Medicines from './components/pages/Medicines';
import AddMedicine from './components/pages/AddMedicine';
import EditMedicine from './components/pages/EditMedicine';
import CreateReport from './components/pages/CreateReport';
import ReportDetail from './components/pages/ReportDetail';

import ClinicSettings from './components/pages/ClinicSettings';
import Reports from './components/pages/Reports';
import ReportArchive from './components/pages/ReportArchive';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Users from './components/pages/Users';
import EditUser from './components/pages/EditUser';
import UserAccessControl from './components/pages/UserAccessControl';
import ProfilePage from './components/auth/ProfilePage';
import ManageRoles from './components/pages/ManageRoles';
import FinancialReports from './components/pages/FinancialReports';
import AppointmentReports from './components/pages/AppointmentReports';
import DoctorPayments from './components/pages/DoctorPayments';
import Expenses from './components/pages/Expenses';
import ExpenseTypes from './components/pages/ExpenseTypes';
import DoctorSalaries from './components/pages/DoctorSalaries';
import DoctorPortal from './components/pages/DoctorPortal';
import DoctorPaymentHistory from './components/pages/DoctorPaymentHistory';
import DoctorPaymentReceipt from './components/pages/DoctorPaymentReceipt';
import SinglePaymentReceipt from './components/pages/SinglePaymentReceipt';
import ProtectedRoute from './components/routing/ProtectedRoute';
import PatientPortal from './components/pages/PatientPortal';
import PatientReportView from './components/pages/PatientReportView';
import ReportRedirect from './components/pages/ReportRedirect';
import EncodedReportView from './components/pages/EncodedReportView';
import SecureReportView from './components/pages/SecureReportView';
import SecureReportViewer from './components/pages/SecureReportViewer';


// New components for receptionist features
import Appointments from './components/pages/Appointments';
import AddAppointment from './components/pages/AddAppointment';
import EditAppointment from './components/pages/EditAppointment';
import OnlineAppointments from './components/pages/OnlineAppointments';
import PatientQueue from './components/pages/PatientQueue';
import Billing from './components/pages/Billing';
import CreateBill from './components/pages/CreateBill';
import BillDetail from './components/pages/BillDetail';
import EditBill from './components/pages/EditBill';
import ProcessPayment from './components/pages/ProcessPayment';
import RefundPayment from './components/pages/RefundPayment';
import PatientTicket from './components/pages/PatientTicket';
import BackupRestore from './components/pages/BackupRestore';
import TokenDisplay from './components/pages/TokenDisplay';
import QueueTicket from './components/pages/QueueTicket';
import GymMembers from './components/pages/GymMembers';
import GymTrainers from './components/pages/GymTrainers';
import GymPackages from './components/pages/GymPackages';
import GymMemberships from './components/pages/GymMemberships';
import GymAttendance from './components/pages/GymAttendance';
import GymPayments from './components/pages/GymPayments';
import GymDashboard from './components/pages/GymDashboard';
import GymMemberDetail from './components/pages/GymMemberDetail';
import GymWorkoutPlans from './components/pages/GymWorkoutPlans';
import GymReports from './components/pages/GymReports';
import GymExpenses from './components/pages/GymExpenses';
import AIMedicalAssistant from './components/pages/AIMedicalAssistant';
import FloatingAIChat from './components/layout/FloatingAIChat';

import QRScanner from './components/pages/QRScanner';
import { AuthProvider } from './context/AuthContext';
import { AppModeProvider } from './context/AppModeContext';

// Layout
import { Container, Row, Col } from 'react-bootstrap';

const DashboardWrapper = ({ clinic }) => {
  const { user } = useContext(AuthContext);
  const { appMode } = useContext(AppModeContext);
  
  if (user?.role === 'patient') {
    return <PatientPortal />;
  }
  
  return (
    <>
      <Navbar clinic={clinic} />
      <Container fluid>
        <Row>
          <Col md={2} lg={2} className="sidebar p-0 d-none d-md-block">
            <Sidebar />
          </Col>
          <Col xs={12} md={10} lg={10} className="content">
            {appMode === 'gym' ? <GymDashboard /> : <Dashboard />}
          </Col>
        </Row>
      </Container>
    </>
  );
};

const App = () => {
  const [clinic, setClinic] = useState({
    name: 'My Clinic',
    address: 'Clinic Address',
    contactNumber: 'Contact Number',
    email: '',
    logo: null
  });

  // Apply global modal fix
  useModalFix();

  useEffect(() => {
    const fetchClinicData = async () => {
      try {
        const res = await axios.get('/api/clinic.php');
        setClinic(res.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchClinicData();
  }, []);

  return (
    <AuthProvider>
      <AppModeProvider>
        <Router>
        <FloatingAIChat />
        <Routes>
          <Route path="/login" element={<Login />} />
          
          {/* MedX Arena Route */}
          <Route path="/medxarena" element={
            <ProtectedRoute>
              <DashboardWrapper clinic={clinic} />
            </ProtectedRoute>
          } />
          
          {/* Protected Routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <DashboardWrapper clinic={clinic} />
            </ProtectedRoute>
          } />
          
          {/* Patient Routes */}
          <Route path="/patients" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <Patients />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/patients/add" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <AddPatient />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/patients/edit/:id" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <EditPatient />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/patients/:id" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <PatientDetail />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          {/* Doctor Routes */}
          <Route path="/doctor-portal/:id" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <DoctorPortal />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/doctors" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <Doctors />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          {/* Appointment Routes */}
          <Route path="/appointments" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <Appointments />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/online-appointments" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <OnlineAppointments />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/appointments/add" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <AddAppointment />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/appointments/edit/:id" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <EditAppointment />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          {/* Patient Queue Routes */}
          <Route path="/queue" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <PatientQueue />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          {/* Billing Routes */}
          <Route path="/billing" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <Billing />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/create-bill" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <CreateBill />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/billing/create/:patientId" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <CreateBill />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/billing/:id" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <BillDetail />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/billing/edit/:id" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <EditBill />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/billing/:id/payment" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <ProcessPayment />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/billing/:id/refund" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <RefundPayment />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          {/* Patient Ticket Routes - Without Admin Layout */}
          <Route path="/ticket/appointment/:id" element={
            <ProtectedRoute>
              <PatientTicket />
            </ProtectedRoute>
          } />
          
          <Route path="/ticket/receipt/:id" element={
            <ProtectedRoute>
              <PatientTicket />
            </ProtectedRoute>
          } />
          
          <Route path="/ticket/queue/:id" element={
            <ProtectedRoute>
              <PatientTicket />
            </ProtectedRoute>
          } />
          
          {/* Token Display Screen - Public */}
          <Route path="/token-display" element={<TokenDisplay />} />
          
          {/* Queue Ticket Print */}
          <Route path="/queue/ticket/:id" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <QueueTicket />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          {/* Other existing routes */}
          <Route path="/doctors/add" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <AddDoctor />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/doctors/edit/:id" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <AddDoctor />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          {/* Rest of the routes remain unchanged */}
          <Route path="/medicines" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <Medicines />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/medicines/add" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <AddMedicine />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/medicines/edit/:id" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <EditMedicine />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/appointment-reports" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <AppointmentReports />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/financial-reports" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <FinancialReports />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/expense-types" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <ExpenseTypes />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/expenses" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <Expenses />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/doctor-payment-history/:id" element={
            <ProtectedRoute>
              <DoctorPaymentHistory />
            </ProtectedRoute>
          } />
          
          <Route path="/doctor-salaries" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <DoctorSalaries />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/doctor-payments" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <DoctorPayments />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/doctor-payment-receipt" element={
            <ProtectedRoute>
              <DoctorPaymentReceipt />
            </ProtectedRoute>
          } />
          
          <Route path="/single-payment-receipt" element={
            <ProtectedRoute>
              <SinglePaymentReceipt />
            </ProtectedRoute>
          } />
          
          <Route path="/reports" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <Reports />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/reports/create/:patientId" element={
            <ProtectedRoute requiredRole="doctor">
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <CreateReport />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/reports/edit/:id" element={
            <ProtectedRoute requiredRole="doctor">
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <CreateReport />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/reports/:id" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <ReportDetail />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/reports/archive" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <ReportArchive />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/settings" element={
            <ProtectedRoute requiredRole="admin">
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <ClinicSettings clinic={clinic} setClinic={setClinic} />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/users" element={
            <ProtectedRoute requiredRole="admin">
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <Users />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/users/edit/:id" element={
            <ProtectedRoute requiredRole="admin">
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <EditUser />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/user-access/:id" element={
            <ProtectedRoute requiredRole="admin">
              <UserAccessControl />
            </ProtectedRoute>
          } />
          
          <Route path="/register" element={
            <ProtectedRoute requiredRole="admin">
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <Register />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/roles" element={
            <ProtectedRoute requiredRole="admin">
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <ManageRoles />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/profile" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <ProfilePage />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />

          <Route path="/patients/archive" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <PatientArchive />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />


          
          {/* Patient Report View Route */}
          <Route path="/patient-report/:id" element={
            <ProtectedRoute>
              <PatientReportView />
            </ProtectedRoute>
          } />
          
          {/* Encoded Report View - Base64 Decoded URL */}
          <Route path="/r/:encoded" element={<EncodedReportView />} />
          
          {/* Secure Report Viewer - Opens report in blank window without URL */}
          <Route path="/s/:encoded" element={<SecureReportViewer />} />
          
          {/* Secure Report View - QR Code Scan (No Navigation, Print Ready) */}
          <Route path="/report/secure/:encodedId" element={<SecureReportView />} />
          
          {/* QR Code Redirect - Opens report in popup without URL bar */}
          <Route path="/qr-report/:id" element={<ReportRedirect />} />
          
          {/* Public Report View - No Login Required - Uses Original ReportDetail */}
          <Route path="/public-report/:id" element={<ReportDetail />} />
          
          {/* Admin Report View Without Layout - For Patient Portal */}
          <Route path="/view-report/:id" element={
            <ProtectedRoute>
              <ReportDetail />
            </ProtectedRoute>
          } />
          
          {/* Backup & Restore Route */}
          <Route path="/backup" element={
            <ProtectedRoute requiredRole="admin">
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <BackupRestore />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          {/* Gym Routes */}
          <Route path="/gym/dashboard" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <GymDashboard />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/gym/members" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <GymMembers />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/gym/trainers" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <GymTrainers />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/gym/packages" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <GymPackages />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/gym/memberships" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <GymMemberships />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/gym/attendance" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <GymAttendance />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/gym/payments" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <GymPayments />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/gym/members/:id" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <GymMemberDetail />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/gym/workout-plans" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <GymWorkoutPlans />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/gym/reports" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <GymReports />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/gym/qr-scanner" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <QRScanner />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/ai-assistant" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <AIMedicalAssistant />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
          
          <Route path="/gym/expenses" element={
            <ProtectedRoute>
              <>
                <Navbar clinic={clinic} />
                <Container fluid>
                  <Row>
                    <Col md={2} className="sidebar p-0">
                      <Sidebar />
                    </Col>
                    <Col md={10} className="content">
                      <GymExpenses />
                    </Col>
                  </Row>
                </Container>
              </>
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
      </AppModeProvider>
    </AuthProvider>
  );
};

export default App; 
